import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useCart } from '../context/CartContext';
import { usePreventBack } from '../hooks/usePreventBack';
import Loading from '../components/Loading';

export default function PaymentStatus() {
    usePreventBack(true);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { clearCart } = useCart();
    const [status, setStatus] = useState('verifying'); // verifying, success, pending, failed, error
    const [orderDetails, setOrderDetails] = useState(null);
    const [retryLoading, setRetryLoading] = useState(false);

    useEffect(() => {
        verifyPayment();
    }, []);

    const verifyPayment = async () => {
        // Get parameters from URL
        const orderId = searchParams.get('order_id');
        const cfOrderId = searchParams.get('cf_order_id') || searchParams.get('orderId');
        const statusParam = searchParams.get('status');
        const rzpPaymentId = searchParams.get('razorpay_payment_id');
        const rzpOrderId = searchParams.get('razorpay_order_id');
        const rzpSignature = searchParams.get('razorpay_signature');

        console.log('Verifying payment:', { orderId, cfOrderId, statusParam, rzpPaymentId });

        if (!orderId && !cfOrderId && !rzpOrderId) {
            setStatus('error');
            return;
        }

        try {
            // If returning from Razorpay modal, call verify-razorpay backend endpoint while displaying loader
            if (rzpPaymentId && orderId) {
                try {
                    await api.post('/api/payments/verify-razorpay', {
                        razorpay_payment_id: rzpPaymentId,
                        razorpay_order_id: rzpOrderId,
                        razorpay_signature: rzpSignature,
                        dbOrderId: orderId
                    });
                } catch (rzpErr) {
                    console.warn('Razorpay verify call note:', rzpErr.message);
                }
            }

            // Check verification status via API
            const targetId = orderId || cfOrderId || rzpOrderId;
            const verifyResponse = await api.get(`/api/payments/verify/${targetId}`);
            
            console.log('Verification response:', verifyResponse.data);

            const isPaid = statusParam === 'success' || 
                           !!rzpPaymentId ||
                           verifyResponse.data.paymentStatus === 'PAID' || 
                           verifyResponse.data.orderStatus === 'PAID';

            if (isPaid) {
                // Clear cart after successful payment (clears DB and React state)
                try {
                    await clearCart();
                } catch (e) {
                    console.log('Cart clear error:', e);
                }

                setStatus('success');
                setOrderDetails(verifyResponse.data);
                
                // Redirect to orders after 2 seconds
                setTimeout(() => navigate('/orders'), 2000);
            } else if (['FAILED', 'CANCELLED', 'EXPIRED'].includes(verifyResponse.data.paymentStatus)) {
                setStatus('failed');
                setOrderDetails(verifyResponse.data);
            } else {
                setStatus('pending');
            }
        } catch (error) {
            console.error('Verification error:', error);
            // If status parameter or Razorpay ID is present, still treat as success
            if (statusParam === 'success' || rzpPaymentId) {
                try { await clearCart(); } catch (e) {}
                setStatus('success');
                setTimeout(() => navigate('/orders'), 2000);
            } else {
                setStatus('error');
            }
        }
    };

    const handleRetry = async () => {
        const orderId = searchParams.get('order_id');
        if (!orderId) return;

        setRetryLoading(true);
        try {
            const response = await api.post(`/api/payments/retry/${orderId}`);
            if (response.data.paymentSessionId) {
                const paymentSessionId = response.data.paymentSessionId;
                const cfOrderId = response.data.orderId; // Use our generated ID string

                // Load Cashfree SDK
                const cashfreeScript = document.createElement('script');
                cashfreeScript.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
                cashfreeScript.async = true;
                document.body.appendChild(cashfreeScript);

                cashfreeScript.onload = () => {
                    const cashfree = window.Cashfree({
                        mode: import.meta.env.VITE_CASHFREE_ENVIRONMENT === 'production' ? 'production' : 'sandbox'
                    });

                    cashfree.checkout({
                        paymentSessionId: paymentSessionId,
                        redirectTarget: '_self',
                        returnUrl: `${window.location.origin}/payment-status?order_id=${orderId}&cf_order_id=${cfOrderId}`
                    });
                };
            }
        } catch (err) {
            console.error('Retry failed:', err);
            toast.error(err.response?.data?.message || 'Failed to re-initiate payment');
        } finally {
            setRetryLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto mt-20 text-center p-8">
            {status === 'verifying' && (
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6 animate-in fade-in duration-300">
                    <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Verifying Payment...</h2>
                        <p className="text-sm text-gray-500 mt-1">Please wait while we confirm your transaction with the payment aggregator.</p>
                    </div>
                </div>
            )}
            
            {status === 'success' && (
                <div className="space-y-4">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
                    <h2 className="text-2xl font-bold text-green-600">Payment Successful!</h2>
                    <p className="text-gray-600">Your order has been confirmed.</p>
                    <p className="text-sm text-gray-500">Redirecting to your orders...</p>
                    <button 
                        onClick={() => navigate('/orders')} 
                        className="btn-primary mt-4"
                    >
                        View Orders
                    </button>
                </div>
            )}
            
            {status === 'pending' && (
                <div className="space-y-4">
                    <Loading mode="page" message="Payment Pending" />
                    <p className="text-gray-600">Your payment is being processed.</p>
                    <p className="text-sm text-gray-500">Please check your orders page in a few minutes.</p>
                    <button 
                        onClick={() => navigate('/orders')} 
                        className="btn-primary mt-4"
                    >
                        View Orders
                    </button>
                </div>
            )}
            
            {status === 'failed' && (
                <div className="space-y-4">
                    <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                    <h2 className="text-xl font-semibold text-red-600">Payment Failed</h2>
                    <p className="text-gray-600">The transaction was unsuccessful or cancelled.</p>
                    <div className="flex flex-col gap-3 mt-4">
                        <button 
                            onClick={handleRetry} 
                            disabled={retryLoading}
                            className="btn-primary w-full flex items-center justify-center gap-2"
                        >
                            {retryLoading ? <Loading mode="button" message="Processing..." /> : 'Retry Payment'}
                        </button>
                        <button 
                            onClick={() => navigate('/orders')} 
                            className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                        >
                            Back to My Orders
                        </button>
                    </div>
                </div>
            )}

            {status === 'error' && (
                <div className="space-y-4">
                    <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                    <h2 className="text-xl font-semibold text-red-600">Verification Failed</h2>
                    <p className="text-gray-600">We couldn't verify your payment status.</p>
                    <div className="bg-yellow-50 p-4 rounded-lg text-sm text-yellow-800">
                        <p className="font-medium">Don't worry!</p>
                        <p>If amount was deducted, your order will be confirmed automatically.</p>
                        <p>Please check your orders page or contact support.</p>
                    </div>
                    <div className="flex gap-4 justify-center mt-4">
                        <button 
                            onClick={() => navigate('/orders')} 
                            className="btn-primary"
                        >
                            View Orders
                        </button>
                        <button 
                            onClick={() => window.location.reload()} 
                            className="btn-secondary"
                        >
                            Retry Verification
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}